import { useRef } from "react";
import { useDroppable } from "@dnd-kit/core";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Badge, Button, Popover, Skeleton, Space, Tooltip, Typography } from "antd";
import * as TbIcons from "react-icons/tb";
import { IconType } from "react-icons";

import { CardDto, ColumnTemplateRef } from "dtos/kanban.dto";
import SortableTaskItem from "./SortableTaskItem";
import TaskItem from "./TaskItem";
import { toTitleCase } from "utils/functions";
import { GroupColor } from "./BoardSectionList";
import { useNavigate } from "react-router-dom";
import { ApiService } from "api/api.service";
import { splitAction } from "./util";
import { resolveStageIcon } from "components/Stages/constants";

type BoardSectionProps = {
  id: string;
  title: string;
  description?: string;
  cards: CardDto[];
  type?: string;
  disabled: boolean;
  isPending?: boolean;
  iconName?: string;
  groupColor: GroupColor;
  templates?: ColumnTemplateRef[];
  orgCode?: string;
  action?: string;
};

const ESTIMATED_CARD_HEIGHT = 200;

const BoardSection = ({
  id,
  title,
  description,
  cards,
  disabled,
  isPending,
  type,
  iconName,
  groupColor,
  templates,
  orgCode,
  action,
}: BoardSectionProps) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  const stages = ApiService.organisation.useGetAllStages(orgCode, { usable: true });
  const linkedStage = splitAction(action);
  const matchingStage = stages.data?.find(
    (s) => s.resource.toLocaleLowerCase() === linkedStage.resource && s.value === linkedStage.stageValue,
  );

  const hasAction = Boolean(action);
  const hasTemplates = Boolean(templates?.length);

  const handleAddTemplate = () => {
    const params = new URLSearchParams();
    params.set("openEditor", "true");
    if (action) params.set("action", action);
    if (orgCode) params.set("orgId", orgCode);
    navigate(`/dashboard/communications/templates?${params.toString()}`);
  };

  const { setNodeRef } = useDroppable({ id, disabled });

  const setRefs = (el: HTMLDivElement | null) => {
    scrollRef.current = el;
    setNodeRef(el);
  };

  const rowVirtualizer = useVirtualizer({
    count: cards.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ESTIMATED_CARD_HEIGHT,
    measureElement:
      typeof ResizeObserver === "undefined" ? undefined : (element) => element?.getBoundingClientRect().height,
    overscan: 5,
  });

  const Icon = (iconName && (TbIcons as Record<string, IconType>)[iconName]) || null;
  const templatePopoverContent = templates?.length ? (
    <div style={{ minWidth: 200, maxWidth: 260 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: "#6b7280",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          marginBottom: 8,
        }}
      >
        Linked Templates
      </div>
      <div className="flex flex-col gap-1.5">
        {templates.map((tmpl) => (
          <div
            key={tmpl._id}
            className="flex items-center gap-2 px-2 py-1.5 rounded-md"
            style={{ background: "#f5f3ff", border: "1px solid #ede9fe" }}
          >
            <TbIcons.TbLink size={13} style={{ color: "#6d28d9", flexShrink: 0 }} />
            <span style={{ fontSize: 12, fontWeight: 500, color: "#374151" }}>{tmpl.name}</span>
          </div>
        ))}
      </div>
    </div>
  ) : null;

  const iconStyle: React.CSSProperties = hasTemplates
    ? { background: "#f5f3ff", border: "1px solid #ede9fe", color: "#6d28d9" }
    : hasAction
      ? { background: "#f3f4f6", border: "1px solid #e5e7eb", color: "#9ca3af" }
      : { background: "#f9fafb", border: "1px solid #f0f0f0", color: "#d1d5db" };

  const popoverContent = (
    <div style={{ minWidth: 220, maxWidth: 280 }}>
      {!hasAction ? (
        <div className="flex flex-col gap-2">
          <div style={{ fontSize: 12, color: "#6b7280" }}>
            No action mapped to this column. Go to{" "}
            <span style={{ fontWeight: 600, color: "#374151" }}>Manage Columns</span> to map an action first.
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {hasTemplates && (
            <>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#6b7280",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                Linked Templates
              </div>
              <div className="flex flex-col gap-1.5">
                {templates!.map((tmpl) => (
                  <div
                    key={tmpl._id}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-md"
                    style={{ background: "#f5f3ff", border: "1px solid #ede9fe" }}
                  >
                    <TbIcons.TbLink size={13} style={{ color: "#6d28d9", flexShrink: 0 }} />
                    <span style={{ fontSize: 12, fontWeight: 500, color: "#374151" }}>{tmpl.name}</span>
                  </div>
                ))}
              </div>
            </>
          )}
          <Button
            size="small"
            type="dashed"
            icon={<TbIcons.TbLink size={13} />}
            onClick={handleAddTemplate}
            style={{
              width: "100%",
              borderColor: "#c4b5fd",
              color: "#6d28d9",
              marginTop: hasTemplates ? 4 : 0,
            }}
          >
            Add Template
          </Button>
        </div>
      )}
    </div>
  );

  const StageIcon = matchingStage ? resolveStageIcon(matchingStage.icon) : () => <div />;

  const stageIndicator = stages.isPending ? (
    <Skeleton.Avatar active size={22} shape="square" />
  ) : matchingStage ? (
    <Tooltip title={`Column mapped to "${matchingStage.value}" stage`}>
      <span className="flex items-center justify-center" style={{ width: 22, height: 22, flexShrink: 0 }}>
        <StageIcon />
      </span>
    </Tooltip>
  ) : (
    <Tooltip title={hasAction ? "Mapped action has no matching usable stage" : "No stage mapped to this column"}>
      <span
        className="flex items-center justify-center rounded-md"
        style={{
          width: 22,
          height: 22,
          flexShrink: 0,
          background: "#f9fafb",
          border: "1px dashed #e5e7eb",
          color: "#d1d5db",
        }}
      >
        <TbIcons.TbCircleDashed size={13} />
      </span>
    </Tooltip>
  );

  return (
    <div
      className={`min-w-[320px] w-[320px] h-full bg-gray-50 p-4 rounded-xl ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
    >
      <div
        ref={setRefs}
        className="overflow-y-auto relative hide-scrollbar"
        style={{ height: "calc(100vh - 280px)", scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        <div className="sticky top-0 z-30 bg-gray-50 pb-2">
          <div className="flex items-center gap-2">
            {stageIndicator}

            <Space className="grow">
              <Typography.Title level={5} style={{ margin: 0 }} ellipsis={{ tooltip: toTitleCase(title) }}>
                {toTitleCase(title)}
              </Typography.Title>
              {Boolean(description?.length) && (
                <Tooltip title={description}>
                  <TbIcons.TbInfoCircle size={16} />
                </Tooltip>
              )}
            </Space>

            <div
              className="min-w-[22px] px-1.5 py-0.5 text-xs flex items-center justify-center rounded-full font-medium"
              style={{ backgroundColor: groupColor.solid, color: "#fff" }}
            >
              {cards.length}
            </div>

            {popoverContent && (
              <Popover
                content={popoverContent}
                trigger="hover"
                placement="bottomRight"
                arrow={false}
                style={{ padding: 12 }}
              >
                <div
                  className="flex items-center justify-center rounded-md cursor-pointer"
                  style={{ width: 22, height: 22, flexShrink: 0, ...iconStyle }}
                >
                  <TbIcons.TbLink size={13} />
                </div>
              </Popover>
            )}

            {disabled && <Badge count="Disabled" style={{ backgroundColor: "#f5222d", color: "#fff" }} />}
          </div>

          <div className="w-full h-[2px] rounded-xl my-2" style={{ backgroundColor: groupColor.border }} />
        </div>

        {isPending ? (
          <TaskCardSkeleton />
        ) : (
          <div style={{ height: rowVirtualizer.getTotalSize(), position: "relative" }}>
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const card = cards[virtualRow.index];

              return (
                <div
                  key={card._id}
                  data-index={virtualRow.index}
                  ref={rowVirtualizer.measureElement}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  <div className="mb-2">
                    <SortableTaskItem id={card._id}>
                      <TaskItem itemData={card} />
                    </SortableTaskItem>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {cards.length === 0 && !isPending && (
          <div className="text-sm text-gray-400 italic py-4 text-center">Drop {type || "Item"} here</div>
        )}
      </div>
    </div>
  );
};

export default BoardSection;

function TaskCardSkeleton() {
  return (
    <div className="w-full bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="flex items-start justify-between px-4 pt-3">
        <div className="flex items-start gap-3 flex-1">
          <Skeleton.Avatar active size="small" shape="square" />
          <div className="flex-1">
            <Skeleton.Input active size="small" style={{ width: 140, marginBottom: 6 }} />
            <Skeleton.Input active size="small" style={{ width: 180 }} />
          </div>
        </div>
        <Skeleton.Avatar active size="small" shape="circle" />
      </div>

      <div className="px-4 py-2 border-t border-gray-100">
        <div className="flex items-center gap-2 mb-3">
          <Skeleton.Avatar active size="small" />
          <Skeleton.Input active size="small" style={{ width: 160 }} />
        </div>
        <div className="flex items-center gap-2 mb-3">
          <Skeleton.Avatar active size="small" />
          <Skeleton.Input active size="small" style={{ width: 100 }} />
        </div>
      </div>

      <div className="px-4 py-3 border-t border-gray-100 bg-gray-50">
        <div className="flex items-center justify-between">
          <Skeleton.Input active size="small" style={{ width: 80 }} />
          <Skeleton.Input active size="small" style={{ width: 70 }} />
        </div>
      </div>
    </div>
  );
}
